// File path: /shell/src/components/ShellLayout.tsx
// Shell Layout - Main layout with module tabs and sidebar

import React, { useState, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@core/context/AuthContext';
import { useSubscription } from '@core/context/SubscriptionContext';
import { useModule } from '../context/ModuleContext';
import { MODULE_REGISTRY } from '@core/config/moduleRegistry';
import type { ModuleId, ModuleSubscription } from '@core/config/moduleRegistry';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import ModuleTabs from './ModuleTabs';
import ModuleSidebar from './ModuleSidebar';

// Default logos (used when no module-specific logo available)
const DefaultLogo = '/logo.png';
const DefaultLogoIcon = '/logo-icon.png';

const ShellLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { getAccessibleModules } = useSubscription();
  const { activeModule, getModuleEntryPoint } = useModule();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get accessible modules with details
  const accessibleModules = useMemo(() => {
    return getAccessibleModules().map((sub: ModuleSubscription) => ({
      ...sub,
      details: MODULE_REGISTRY[sub.moduleId]
    }));
  }, [getAccessibleModules]);

  // Get user's highest role
  const getUserRole = () => {
    if (!user?.groups) return 'Teacher';
    const roleHierarchy = ['SuperAdmin', 'StateAdmin', 'ProgramAdmin', 'DistrictAdmin', 'SchoolAdmin', 'Coach', 'Teacher'];
    for (const role of roleHierarchy) {
      if (user.groups.includes(role)) {
        return role;
      }
    }
    return 'Teacher';
  };

  const userRole = getUserRole();

  // Get navigation items for the active module
  const navigationItems = useMemo(() => {
    if (!activeModule) return [];

    const moduleEntry = getModuleEntryPoint(activeModule);
    if (!moduleEntry) return [];

    return moduleEntry.getNavigationItems(userRole);
  }, [activeModule, userRole, getModuleEntryPoint]);

  // Get current page title
  const currentPageTitle = useMemo(() => {
    const currentPath = location.pathname;
    // Extract the path after module ID
    const moduleBasePath = activeModule ? `/${activeModule}` : '';
    const relativePath = currentPath.replace(moduleBasePath, '').replace(/^\//, '');

    const navItem = navigationItems.find(item =>
      item.href === relativePath ||
      currentPath.endsWith(item.href)
    );

    return navItem?.name || 'Dashboard';
  }, [location.pathname, activeModule, navigationItems]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get active module color and logos
  const moduleColor = activeModule ? MODULE_REGISTRY[activeModule]?.color : '#2CB1BC';
  const moduleLogo = activeModule ? MODULE_REGISTRY[activeModule]?.logo : undefined;
  const moduleLogoIcon = activeModule ? MODULE_REGISTRY[activeModule]?.logoIcon : undefined;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ backgroundColor: moduleColor }}
      >
        {/* Logo and collapse button */}
        <div
          className="flex items-center justify-between h-16 px-4"
          style={{ backgroundColor: `color-mix(in srgb, ${moduleColor} 85%, black)` }}
        >
          <Link to="/" className="flex items-center overflow-hidden">
            {isSidebarCollapsed ? (
              <img
                className="h-8 w-8 flex-shrink-0"
                src={moduleLogoIcon || DefaultLogoIcon}
                alt=""
                onError={(e) => {
                  e.currentTarget.src = '/logo-icon.png';
                }}
              />
            ) : (
              <>
                <img
                  className="h-8 w-auto flex-shrink-0 max-w-[140px]"
                  src={moduleLogo || DefaultLogo}
                  alt="LumiLearn"
                  onError={(e) => {
                    e.currentTarget.src = '/logo.png';
                  }}
                />
              </>
            )}
          </Link>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="text-white/70 hover:text-white transition-colors"
          >
            {isSidebarCollapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Module Sidebar Navigation */}
        <ModuleSidebar
          navigationItems={navigationItems}
          moduleId={activeModule}
          isSidebarCollapsed={isSidebarCollapsed}
          moduleColor={moduleColor}
        />

        {/* Logout button */}
        <div className="px-2 py-4 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="group flex items-center w-full px-2 py-2 text-sm font-medium text-white/80 rounded-md hover:bg-white/10 hover:text-white transition-colors"
            title={isSidebarCollapsed ? 'Logout' : undefined}
          >
            <ArrowRightOnRectangleIcon
              className={`h-5 w-5 text-white/60 group-hover:text-white ${
                isSidebarCollapsed ? 'mx-auto' : 'mr-3'
              }`}
            />
            {!isSidebarCollapsed && 'Logout'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Header with Module Tabs */}
        <header className="bg-white shadow">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* Module Tabs */}
            <ModuleTabs
              accessibleModules={accessibleModules}
              activeModule={activeModule}
            />

            {/* Page Title Bar */}
            <div className="flex items-center justify-between h-12 border-t border-gray-100">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500"
              >
                <span className="sr-only">Open main menu</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Page title */}
              <div className="flex-1">
                <h1 className="text-lg font-semibold text-gray-900">
                  {currentPageTitle}
                </h1>
              </div>

              {/* Right side items */}
              <div className="flex items-center space-x-4">
                {/* User menu */}
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                    <span className="sr-only">Open user menu</span>
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: moduleColor }}
                    >
                      {user?.given_name?.[0]}{user?.family_name?.[0]}
                    </div>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="px-4 py-2 border-b">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.given_name} {user?.family_name}
                        </p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                        <p className="text-xs font-medium" style={{ color: moduleColor }}>{userRole}</p>
                      </div>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={`/${activeModule || 'elevate'}/profile`}
                            className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            <UserCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                            Your Profile
                          </Link>
                        )}
                      </Menu.Item>
                      {/* Modules Section */}
                      <div className="px-4 py-2 border-t border-b border-gray-100">
                        <div className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          <Squares2X2Icon className="mr-2 h-4 w-4" />
                          Your Modules
                        </div>
                        {accessibleModules.length > 0 ? (
                          <div className="space-y-1">
                            {accessibleModules.map((module) => (
                              <div
                                key={module.moduleId}
                                className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-50"
                              >
                                <div
                                  className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                                  style={{ backgroundColor: module.details?.color || '#6b7280' }}
                                />
                                <span className="text-sm text-gray-700">{module.moduleName}</span>
                                <span className="ml-auto text-xs text-gray-400 capitalize">{module.tier}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No modules assigned</p>
                        )}
                      </div>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                          >
                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
          <nav
            className="fixed top-0 left-0 bottom-0 flex flex-col w-64"
            style={{ backgroundColor: moduleColor }}
          >
            <div
              className="flex items-center justify-between h-16 px-4"
              style={{ backgroundColor: `color-mix(in srgb, ${moduleColor} 85%, black)` }}
            >
              <img
                className="h-8 w-auto max-w-[140px]"
                src={moduleLogo || DefaultLogo}
                alt="LumiLearn"
                onError={(e) => {
                  e.currentTarget.src = '/logo.png';
                }}
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white/70 hover:text-white"
              >
                <span className="sr-only">Close menu</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ModuleSidebar
              navigationItems={navigationItems}
              moduleId={activeModule}
              isSidebarCollapsed={false}
              moduleColor={moduleColor}
              onItemClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="px-2 py-4 border-t border-white/20">
              <button
                onClick={handleLogout}
                className="group flex items-center w-full px-2 py-2 text-base font-medium text-white/80 rounded-md hover:bg-white/10 hover:text-white"
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-6 w-6 text-white/60 group-hover:text-white" />
                Logout
              </button>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ShellLayout;
